import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Globe } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const loc = useLocation();
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <header ref={ref} className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-2xl md:max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
          <div className="relative w-8 h-8 rounded-lg overflow-hidden auk-ribbon">
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-black text-sm" style={{textShadow: "0 1px 2px rgba(0,0,0,0.5)"}}>A</span>
            </div>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-base font-extrabold tracking-tight">ANGOLA<span className="text-[#c8102e]">UK+</span></span>
            <span className="text-[10px] text-zinc-500 -mt-0.5">{t("tagline")}</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-xs font-semibold" data-testid="header-web-nav">
          {[["/comunidade", "Comunidade"], ["/eventos", "Eventos"], ["/marketplace", "Marketplace"], ["/empregos", "Empregos"]].map(([to, l]) => (
            <Link key={to} to={to} className={`px-2.5 py-1.5 rounded-full hover:bg-zinc-100 ${loc.pathname.startsWith(to) ? "text-[#c8102e]" : "text-zinc-700"}`}>{l}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "pt" ? "en" : "pt")}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-zinc-200 hover:border-black transition-colors"
          >
            <Globe size={12} />
            {lang.toUpperCase()}
          </button>
          {user && <NotificationsBell />}
          {!user && loc.pathname !== "/login" && (
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="ml-1 px-3 py-1.5 rounded-full bg-black text-white text-xs font-semibold hover:bg-[#c8102e] transition-colors"
            >
              {t("login")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
